import * as React from 'react';
import { Avatar, Box, Card, CardActionArea, CardContent, Typography, CardMedia, Chip, Stack } from "@mui/material";
import cardActionAreaClasses from '@mui/material/CardActionArea/cardActionAreaClasses';
import { alpha } from '@mui/material/styles';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { apiBaseUrl } from '../../utils/Api/Api';
import ProductCard from './ProductCard';
import { gray25, layoutPillRadius, lead600, starRating } from '../../theme/colors';
import { formatProviderDeliveryLine } from '../../utils/formatDistance';

const providerCardRippleColor = alpha('#BAA692', 0.5);
const VERTICAL_PRODUCT_LIMIT = 3;
const HORIZONTAL_EAGER_PRODUCT_COUNT = 7;
const HORIZONTAL_LAZY_PRODUCT_COUNT = 5;

const moreInShopSx = {
  fontFamily: 'Nunito',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
  color: lead600,
};

const ProviderProductList = ({ products, isList, highlightTags, eagerCount, hiddenCount }) => {
  if (isList) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
        {products.map((product) => (
          <ProductCard
            key={product.id}
            variant="whiteLi"
            product={product}
            highlightTags={highlightTags}
          />
        ))}
        {hiddenCount > 0 && (
          <Typography sx={moreInShopSx}>Ver mais na loja</Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
      <Stack
        direction="row"
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          pb: 0.5,
          boxSizing: 'border-box',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)' },
          '&::after': {
            content: '""',
            flex: '0 0 32px',
          },
        }}
      >
        {products.map((product, index) => (
          <Box
            key={product.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              alignSelf: 'stretch',
              flex: '0 0 auto',
              width: '172.5px',
              minWidth: '172.5px',
            }}
          >
            <ProductCard
              variant="whiteCard"
              detailMode="weight"
              product={product}
              showCartControls={false}
              imageLoading={index >= eagerCount ? 'lazy' : 'eager'}
              sx={{ width: '100%', height: '100%', boxSizing: 'border-box' }}
            />
          </Box>
        ))}
      </Stack>
      {hiddenCount > 0 && (
        <Typography sx={{ ...moreInShopSx, mt: 1 }}>Ver mais na loja</Typography>
      )}
    </Box>
  );
};

const ProviderCard = ({
  provider,
  onCardClick,
  showProducts,
  showBanner,
  categories,
  highlightTags = [],
  productLayout = 'vertical',
}) => {
  const providerRating = Number(provider.rating || 5).toFixed(1);
  const isList = productLayout === 'vertical';
  const visibleLimit = isList
    ? VERTICAL_PRODUCT_LIMIT
    : HORIZONTAL_EAGER_PRODUCT_COUNT + HORIZONTAL_LAZY_PRODUCT_COUNT;
  const products = provider.products || [];
  const visibleProducts = products.slice(0, visibleLimit);
  const hiddenCount = Math.max(0, products.length - visibleLimit);

  return (
    <Box sx={{ width: '100%' }}>

      {/* Loja e Produtos */}
      <Card
        sx={(theme) => ({
          overflow: 'hidden',
          boxShadow: theme.shadows[2],
          transition: theme.transitions.create('box-shadow', {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': {
            boxShadow: theme.shadows[6],
          },
        })}
      >
        {/* Flexinha */}
        <CardActionArea
          data-no-shadow="true"
          onClick={onCardClick}
          TouchRippleProps={{ style: { color: providerCardRippleColor } }}
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            [`&:hover .${cardActionAreaClasses.focusHighlight}`]: {
              opacity: '0 !important',
            },
            [`&.${cardActionAreaClasses.focusVisible} .${cardActionAreaClasses.focusHighlight}`]: {
              opacity: 0,
            },
            [`& .${cardActionAreaClasses.focusHighlight}`]: {
              opacity: 0,
              backgroundColor: 'transparent',
            },
          }}
        >
          <CardContent sx={{ p: 0, width: '100%', maxWidth: '100%', '&:last-child': { pb: 0 } }}>
            {showBanner && provider.business_banner && (
              <CardMedia
                component="img"
                image={`${apiBaseUrl}${provider.business_banner}`}
                alt="Banner"
                sx={{ width: '100%', height: 'auto', maxHeight: '128px', objectFit: 'cover', borderRadius: '24px', mb: 2 }}
              />
            )}
            {/* Loja */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Logo */}
              <Avatar src={provider.business_logo && `${apiBaseUrl}${provider.business_logo}`} sx={{height: '43px', width: '43px'}}/>

              {/* Infos */}
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 1, flex: '1 0 0', minWidth: 0 }}>

                {/* Nome */}
                <Typography
                  variant="pageTitle"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {provider.name}
                </Typography>

                {/* Rating */}
                <Box sx={{ color: starRating, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 0.25 }}>
                  <StarRoundedIcon sx={{ fontSize: '20px' }} />
                  <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: lead600 }}>{providerRating}
                  </Typography>
                </Box>

                {/* Entrega */}
                <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', lineHeight: '21px', fontWeight: 400, color: lead600 }}>
                  {formatProviderDeliveryLine(provider.distance_km)}
                </Typography>

                {/* Tags do produto */}
                {categories && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: 'Nunito', width: 'fit-content', gap: '4px' }}>
                    <Chip label='Petshop'
                      sx={{ py: '6px', px: '8px', bgcolor: gray25, borderRadius: layoutPillRadius, color: lead600, fontWeight: 400, lineHeight: '14px', fontSize: '14px' }} />
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>

          {showProducts && visibleProducts.length > 0 && (
            <CardContent sx={{ p: 0, width: '100%', maxWidth: '100%', '&:last-child': { pb: 0 } }}>
              <ProviderProductList
                products={visibleProducts}
                isList={isList}
                highlightTags={highlightTags}
                eagerCount={HORIZONTAL_EAGER_PRODUCT_COUNT}
                hiddenCount={hiddenCount}
              />
            </CardContent>
          )}
        </CardActionArea>
      </Card>
    </Box >
  );
};

export default ProviderCard;
